import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { LucideAngularModule } from 'lucide-angular';
import { SpinnerComponent } from '../../../components/spinner/spinner.component';
import { Icons } from '../../../../shared/helpers/icons';
import { UserDataService } from '../../../../shared/services/userData.service';
import { IHazardousMaterial } from '../../../../shared/models/hazardousMaterial';
import { HazardousMaterialsService } from '../../../../shared/services/hazardousMaterials.service';

@Component({
  selector: 'hazardous-material-form',
  standalone: true,
  imports: [CommonModule, FormsModule, LucideAngularModule, SpinnerComponent],
  templateUrl: './hazardous-material-form.component.html',
  styleUrl: './hazardous-material-form.component.css'
})
export class HazardousMaterialFormComponent implements OnInit {

  icons = Icons;

  // When provided, the modal edits this row; otherwise it creates a new one.
  @Input() itemToEdit: IHazardousMaterial | null = null;

  @Output() onClose = new EventEmitter<void>();
  @Output() onSave = new EventEmitter<{ type: string, message: string }>();

  currentBuildingId: string | null = null;
  material: IHazardousMaterial = {};
  loading: boolean = false;

  constructor(
    private userDataService: UserDataService,
    private hazardousMaterialsService: HazardousMaterialsService
  ) {}

  ngOnInit(): void {
    this.currentBuildingId = this.userDataService.getBuildingId();
    // Clone so edits don't mutate the list row until saved.
    this.material = this.itemToEdit ? { ...this.itemToEdit } : {};
  }

  get isEdit(): boolean {
    return !!this.itemToEdit?.id;
  }

  get isValid(): boolean {
    return !!(this.material.name_of_chemical && this.material.quantity && this.material.location);
  }

  closeModal(): void {
    this.onClose.emit();
  }

  save(): void {
    if (!this.currentBuildingId || !this.isValid) return;

    const payload: Partial<IHazardousMaterial> = {
      name_of_chemical: this.material.name_of_chemical,
      quantity: this.material.quantity,
      location: this.material.location,
    };

    this.loading = true;
    const request$ = this.isEdit
      ? this.hazardousMaterialsService.updateHazardousMaterial(this.currentBuildingId, String(this.itemToEdit!.id), payload)
      : this.hazardousMaterialsService.createHazardousMaterial(this.currentBuildingId, payload);

    request$.subscribe({
      next: (response) => {
        this.loading = false;
        if (!response.has_error) {
          this.onSave.emit({
            type: 'success',
            message: this.isEdit ? 'This occurred after updating a hazardous material.' : 'This occurred after adding a hazardous material.'
          });
        } else {
          this.onSave.emit({ type: 'unsuccess', message: response.message });
        }
      },
      error: (err) => {
        this.loading = false;
        this.onSave.emit({ type: 'unsuccess', message: err.message });
      }
    });
  }
}
